<template>
  <div>
    <validation-observer ref="transactionFormValidator">
      <v-form v-if="forms">
        <h2>
          <span v-if="!forms.deal_no && title==='売上編集'">
            受注からの売上登録
          </span>
          <span v-else>
            {{title}}
          </span>
        </h2>
        <v-card
          class="rounded-card"
          flat
        >
          <v-card-text>
            <v-row>
              <v-col
                v-if="forms.deal_no"
                cols="12"
                md="4"
              >
                <div class="overline">
                  取引番号
                </div>
                <h4>{{ forms.deal_no }}</h4>
                <v-divider />
              </v-col>
              <v-col
                v-if="forms.order_no"
                cols="12"
                md="4"
              >
                <div class="overline">
                  受注番号
                </div>
                <h4>{{ forms.order_no }}</h4>
                <v-divider />
              </v-col>
              <v-col
                v-if="forms.request_no"
                cols="12"
                md="4"
              >
                <div class="overline">
                  依頼番号
                </div>
                <h4>{{ forms.request_no }}</h4>
                <v-divider />
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-text class="pb-0">
            <v-row>
              <v-col md="6">
                <validation-provider
                  v-slot="{ errors }"
                  rules="max:12"
                  name="営業担当者"
                  :vid="`sales_staff`"
                >
                  <v-combobox
                    v-model="forms.sales_staff"
                    :error-messages="errors"
                    :items="salesStaffs"
                    :return-object="false"
                    dense
                    label="営業担当者"
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                  >
                    <template v-slot:label>
                      <span>営業担当者</span>
                      <span class="ml-1">
                        ({{ forms.sales_staff?.length || 0 }} / 12)
                      </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-combobox>
                </validation-provider>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-title>
            <validation-provider
              v-slot="{ errors }"
              rules="required"
              class="requiredMark"
              name="取引先"
              :custom-messages="{ required: '取引先が選択されていません' }"
            >
              <span style="color: #0074BA">取引先</span>
              <v-input
                v-model="forms.supplierable_id"
                dense
                hide-details="auto"
                :error-messages="errors"
              />
            </validation-provider>
          </v-card-title>

          <v-card-text>
            <!-- 取引先検索 -->
            <v-row>
              <v-col cols="12" md="6">
                <validation-provider
                  v-slot="{ errors }"
                  class="requiredMark"
                  name="取引先"
                  rules="required"
                >
                  <v-text-field
                    v-model="supplier.name"
                    :disabled="!!$route.params.id"
                    clearable
                    dense
                    filled
                    :error-messages="errors"
                    hide-details="auto"
                    label="取引先"
                    outlined
                    persistent-hint
                    persistent-placeholder
                    readonly
                    style="font-size: 80%"
                    @click:clear="supplierClearButtonClickEvent"
                  >
                    <template v-slot:append-outer>
                      <choose-supplier-button
                        :disabled="!!$route.params.id"
                        :small-button="true"
                        :without-invisible="true"
                        with-none-location
                        @choseSupplierCallback="choseSupplierCallback"
                      />
                    </template>
                  </v-text-field>
                </validation-provider>
                <v-row v-if="supplier.name" dense>
                  <v-col>
                    <ul style="list-style: none">
                      <li>
                        〒{{ supplier.zip_code }}&emsp;{{ supplier.address }}
                      </li>
                      <li>
                        電話番号:{{ supplier.phone_number || '-' }}
                      </li>
                    </ul>
                  </v-col>

                </v-row>
              </v-col>

              <v-col cols="12" md="6" v-if="forms.supplierable_location_code || !location">
                <validation-provider
                  v-slot="{ errors }"
                  name="事業場"
                >
                  <v-select
                    v-model="forms.supplierable_location_code"
                    :disabled="!!$route.params.id"
                    :error-messages="errors"
                    :items="toTraderLocations"
                    clearable
                    dense
                    outlined
                    item-text="name"
                    item-value="code"
                    hide-details="auto"
                    label="事業場"
                    persistent-placeholder
                    @click:clear="locationableClearButtonClickEvent"
                  >
                    <template v-slot:item="slotProps" >
                      <v-chip
                        :title="slotProps.item.code"
                        color="#e0ffff"
                        drak
                        label
                        small
                      >
                        {{ slotProps.item.code }}
                      </v-chip>&nbsp;
                      {{slotProps.item.name}}&nbsp;
                      <small class="text--disabled">{{slotProps.item.address | truncate(20)}} </small>
                    </template>
                  </v-select>
                </validation-provider>
                <v-row v-if="location" dense>
                  <v-col>
                    <ul style="list-style: none">
                      <li>
                        〒{{ location.zip_code }}&emsp;{{ location.address }}
                      </li>
                      <li>
                        電話番号:{{ location.phone_number || '-' }}
                      </li>
                    </ul>
                  </v-col>
                </v-row>
              </v-col>

              <v-col cols="12" md="6" v-if="!forms.supplierable_location_code && location">
                <h4>スポット事業場</h4>
                <template>
                  <ul style="list-style: none">
                    <li>
                      <h3>{{ location?.name }}</h3>
                    </li>
                    <li>
                      〒{{ location?.zip_code }}&emsp;{{ location?.address }}
                    </li>
                    <li>
                      電話番号:{{ location?.phone_number || '-' }}
                    </li>
                  </ul>
                </template>
              </v-col>
            </v-row>
            <!-- /取引先検索 -->

            <!-- 請求先情報 -->
            <v-row v-if="supplier.name">
              <v-col cols="12" md="6">
                <h3>請求先</h3>
                <validation-provider
                  rules="required"
                  :custom-messages="null"
                >
                  <v-input
                    v-model="billingAddressId"
                    hide-details
                  >
                  </v-input>
                </validation-provider>
                <template v-if="billingAddress">
                  <ul style="list-style: none">
                    <li>
                      <h3>{{ billingAddress.name }}</h3>
                    </li>
                    <li>
                      <span v-if="billingAddress.zip_code">〒&nbsp;{{ billingAddress.zip_code }}</span>&emsp;{{ billingAddress.address }}
                    </li>
                    <li>
                      電話番号:&nbsp;{{ billingAddress.phone_number || '-' }}
                    </li>
                    <li>
                      締め日:&nbsp;{{ billingAddress.close_day_type_name || '-' }}
                    </li>
                    <li>
                      相殺/別建区分:&nbsp;{{ billingAddress.counterbalance_type_name || '-' }}
                    </li>
                    <li>
                      メモ:&nbsp;{{ billingAddress.memo || '-' }}
                    </li>
                  </ul>
                </template>
                <h3
                  v-else
                  class="error--text"
                >
                  対象の取引先は、請求先マスタが設定されていませんので売上登録が行えません。
                </h3>
              </v-col>
            </v-row>
            <!-- /請求先情報 -->

            <v-row>
              <v-col cols="12" md="3">
                <validation-provider
                  v-slot="{ errors }"
                  class="requiredMark"
                  rules="required"
                  name="現掛区分"
                >
                  <v-select
                    v-model="forms.receivable_type"
                    :error-messages="errors"
                    :items="receivable_types"
                    dense
                    label="現掛区分"
                    outlined
                    persistent-placeholder
                    hide-details="auto"
                  />
                </validation-provider>
              </v-col>
              <v-col cols="12" md="3">
                <validation-provider
                  v-slot="{ errors }"
                  class="requiredMark"
                  rules="required"
                  name="伝票発行有無"
                >
                  <v-select
                    v-model="forms.issue_voucher"
                    :error-messages="errors"
                    :items="issueVouchers"
                    dense
                    label="伝票発行有無"
                    outlined
                    persistent-placeholder
                    hide-details="auto"
                  />
                </validation-provider>
              </v-col>
            </v-row>

            <v-row>
              <!-- 取引日 -->
              <v-col cols="12" md="3">
                <cs-date-picker
                  :date.sync="forms.trading_date"
                  :minimum-date="minTradingDate"
                  :maximum-date="maxTradingDate"
                  @dateChangedCallback="tradingDateChangedCallback"
                >
                  <template v-slot="{ on, attrs }">
                    <validation-provider
                      v-slot="{ errors }"
                      :rules="{required: true}"
                      name="取引日"
                      class="requiredMark"
                    >
                      <v-text-field
                        v-model="forms.trading_date"
                        v-bind="attrs"
                        v-on="on"
                        :error-messages="errors"
                        append-icon="mdi-calendar"
                        clearable
                        dense
                        hide-details="auto"
                        label="取引日"
                        outlined
                        persistent-hint
                        persistent-placeholder
                        readonly
                      />
                    </validation-provider>
                  </template>
                </cs-date-picker>
              </v-col>
              <!-- /取引日 -->

              <!-- 売上計上日 -->
              <v-col cols="12" md="3">
                <cs-date-picker
                  :date.sync="forms.sales_date"
                  :minimum-date="minSalesDate"
                  :maximum-date="maxSalesDate"
                  @dateChangedCallback="salesDateChangedCallback"
                >
                  <template v-slot="{ on, attrs }">
                    <validation-provider
                      v-slot="{ errors }"
                      :rules="{required: true}"
                      name="売上計上日"
                      class="requiredMark"
                    >
                      <v-text-field
                        v-model="forms.sales_date"
                        v-bind="attrs"
                        v-on="on"
                        :error-messages="errors"
                        append-icon="mdi-calendar"
                        clearable
                        dense
                        hide-details="auto"
                        label="売上計上日"
                        outlined
                        persistent-hint
                        persistent-placeholder
                        readonly
                      />
                    </validation-provider>
                  </template>
                </cs-date-picker>
              </v-col>
              <!-- /売上計上日 -->
            </v-row>

            <!-- 取引一覧 -->
            <v-card
              class="mb-5"
              flat
              tile
            >
              <v-card-title>
                <validation-provider
                  v-slot="{ errors }"
                  class="requiredMark"
                  name="売上明細"
                  rules="required"
                >
                  <v-input
                    v-model="listSize"
                    :error-messages="errors"
                    dense
                    label="売上明細"
                    outlined
                    readonly
                  />
                </validation-provider>
              </v-card-title>
              <v-card-text>
                <div class="text-right">
                  <v-btn
                    :disabled="!forms.supplierable_id || !forms.trading_date || forms.deal_details.filter(v => v._destroy !== true).length >= 50"
                    class="primary"
                    @click="showDialogButtonClickEvent"
                  >
                    売上明細の追加
                  </v-btn>
                </div>
              </v-card-text>
              <v-card-text>
                <v-data-table
                  :headers="tableHeaders"
                  :items="syncTransactionDetails"
                  :items-per-page="-1"
                  item-key="key"
                  dense
                  disable-sort
                  hide-default-footer
                >
                  <template v-slot:[`item.trading_date`]="{item}">
                    {{ item.trading_date | date_format }}
                  </template>
                  <template v-slot:[`item.sales_date`]="{item}">
                    {{ item.sales_date | date_format }}
                  </template>
                  <template v-slot:[`item.tax_type`]="{ item }">
                    {{ item.tax_type | tax_type }}
                  </template>
                  <template v-slot:[`item.quantity`]="{ item }">
                    {{ parseFloat(item.quantity) | comma_format }}
                  </template>
                  <template v-slot:[`item.unit_price`]="{ item }">
                    {{ (item.unit_price === null ? null : parseFloat(item.unit_price)) | comma_format }}
                  </template>
                  <template v-slot:[`item.detailed_amount`]="{ item }">
                    {{ parseFloat(item.detailed_amount) | comma_format }}
                  </template>
                  <!-- 明細備考 -->
                  <template v-slot:[`item.detailed_memo`]="{ item }">
                    <v-tooltip
                      v-if="item.detailed_memo"
                      top
                      :open-on-hover="false"
                    >
                      <template #activator="{ on }">
                        <v-btn
                          icon
                          tile
                          retain-focus-on-click
                          @click="on.click"
                          @blur="on.blur"
                        >
                          <v-icon>mdi-message-text-outline</v-icon>
                        </v-btn>
                      </template>
                      <div>
                        <small>{{ item.detailed_memo }}</small>
                      </div>
                    </v-tooltip>
                  </template>
                  <!-- /明細備考 -->
                  <template v-slot:[`item.editable`]="{ item }">
                    <v-btn
                      class="primary"
                      :disabled="!forms.supplierable_id || !forms.trading_date"
                      small
                      @click="editDialogButtonClickEvent(item)"
                    >
                      編集
                    </v-btn>
                    &nbsp;
                    <v-btn
                      class="error"
                      small
                      @click="deleteButtonClickEvent(item)"
                    >
                      削除
                    </v-btn>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
            <!-- /回収一覧 -->

            <v-divider />
            <v-row>
              <v-col cols="12" md="6" offset-md="6">
                <v-simple-table>
                  <thead>
                    <tr>
                      <th class="text-right">
                        小計金額（税抜）
                      </th>
                      <th class="text-right">
                        消費税金額
                      </th>
                      <th class="text-right">
                        非課税対象
                      </th>
                      <th class="text-right">
                        合計金額
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td class="text-right">
                        {{ subtotalAmount | comma_format }}
                      </td>
                      <td class="text-right">
                        {{ salesTaxAmount | comma_format }}
                      </td>
                      <td class="text-right">
                        {{ taxExemptAmount | comma_format }}
                      </td>
                      <td class="text-right font-weight-black">
                        {{ totalAmount | comma_format }}
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-row>
              <v-col class="text-right">
                <v-btn
                  @click="cancelButtonClickEvent"
                >
                  閉じる
                </v-btn>&nbsp;
                <v-btn
                  class="primary"
                  :disabled="!billingAddress"
                  @click="requestButtonClickEvent"
                >
                  本登録
                </v-btn>&nbsp;
                <v-btn
                  v-if="['unregistered', 'provisional'].includes(forms.status)"
                  class="primary"
                  :disabled="!billingAddress"
                  @click="draftSaveButtonClickEvent"
                >
                  仮保存
                </v-btn>
              </v-col>
            </v-row>
          </v-card-actions>
        </v-card>
      </v-form>
    </validation-observer>
    <transaction-detail-dialog
      :enable-dialog="enableDetailDialog"
      :supplier-id="targetSupplierId"
      :supplier-type="targetSupplierType"
      :fraction-type="targetFractionType"
      :transaction-detail="targetItem"
      :sales-item-units="salesItemUnits"
      :target-date="targetDate"
      @closeEvent="dialogCloseButtonCallback"
      @reflectEvent="dialogReflectButtonCallback"
    />
    <confirm ref="confirm" />
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import PrefectureList from '../../../../mixins/PrefectureList'
import Confirm from '../../../../components/Confirm'
import TransactionDetailDialog from './dialogs/_TransactionDetail'
import ChooseSupplierButton from "@/driver/components/ChooseSupplierButton";


import _ from 'lodash'
export default {
  name: 'DriverSalesTransactionForm',
  components: {
    ChooseSupplierButton,
    Confirm,
    TransactionDetailDialog
  },
  mixins: [
    PrefectureList
  ],
  props: {
    title: {
      type: String,
      default: () => null,
    },
  },
  data() {
    return {
      /** 取引先の詳細情報 */
      supplier: {
        name: null,
      },
      location: null,
      /** 請求先の詳細情報 */
      billingAddress: null,
      billingAddressId: null,
      /** 営業担当者 */
      salesStaffs: [],
      /** 事業場 */
      toTraderLocations: [],
      /** 現掛区分 */
      receivable_types:[],
      /** 伝票発行有無 */
      issueVouchers: [
        { value: true, text: '発行する' },
        { value: false, text: '発行しない' },
      ],
      summaryHeaders: [
        { text: '小計金額（税抜）', value: 'subtotal_amount', align: 'end' },
        { text: '消費税金額', value: 'subtotal_tax_setting', align: 'end' },
        { text: '非課税対象', value: 'tax_exempt_amount', align: 'end' },
        { text: '合計金額', value: 'total_amount', align: 'end' },
      ],
      tableHeaders: [
        { text: '売上品目コード', value: 'sales_item_price_code' },
        { text: '売上品目名', value: 'sales_item_price_name' },
        { text: '取引数量', value: 'quantity', align: 'end' },
        { text: '取引単位', value: 'sales_item_unit_name', align: 'end' },
        { text: '単価', value: 'unit_price', align: 'end' },
        { text: '単位あたり', value: 'quantity_per_unit', align: 'end' },
        { text: '課税区分', value: 'tax_type' },
        { text: '明細金額', value: 'detailed_amount', align: 'end' },
        { text: '明細備考', value: 'detailed_memo', align: 'end' },
        { value: 'editable', text: '編集', align: 'end', },
      ],
      forms: null,
      /** 品目一覧の有無チェック (booleanだとエラー出力できないため、null か 1 で判別) */
      targetSupplierId: null,
      targetSupplierType: null,
      targetFractionType: null,
      targetItem: null,
      targetDate: null,
      enableDetailDialog: false,
      listSize: 0,
      subtotalAmount: 0,
      salesTaxAmount: 0,
      taxExemptAmount: 0,
      totalAmount: 0,
      /** 売上品目 */
      salesItemPriceDetails: [],
      check_deal_details: [],
      before_trading_date: null,
      /** 売上品目単位 */
      salesItemUnits: [],
    }
  },
  computed: {
    syncTransactionDetails () {
      let returnList = []
      for (const detail of this.forms.deal_details) {
        if (!detail._destroy) {
          returnList.push(detail)
        }
      }
      this.setListSize(returnList)
      this.calculateAmounts()
      return returnList
    },
    /**
     * 取引日
     * 最小日付を算出
     */
    minTradingDate () {
      return this.$dayjs(this.forms.created_date).subtract(10, 'years').format('YYYY-MM-DD')
    },
    /**
     * 取引日
     * 最大日付を算出
     */
    maxTradingDate () {
      return this.$dayjs(this.forms.created_date).add(5, 'years').format('YYYY-MM-DD')
    },
    /**
     * 売上計上日
     * 最小日付を算出
     */
    minSalesDate () {
      return this.$dayjs(this.forms.created_date).subtract(10, 'years').format('YYYY-MM-DD')
    },
    /**
     * 売上計上日
     * 最大日付を算出
     */
    maxSalesDate () {
      return this.$dayjs(this.forms.created_date).add(5, 'years').format('YYYY-MM-DD')
    },
  },
  watch: {
    'forms.trading_date': function (newValue, oldValue) {
      this.tradingDateChangeEvent(newValue, oldValue)
    },
    'forms.supplierable_location_code': function (supplierable_location_code) {
      if (supplierable_location_code) {
        if (this.toTraderLocations.length > 0) {
          this.location = this.toTraderLocations.find(v => v.code === supplierable_location_code )
        }
      }
    },
  },
  created() {
    this.fetchSalesStaffs().then((content) => {
      this.salesStaffs = content.sales_staffs
    })
    this.fetchSalesItemUnits().then((content) => {
      this.salesItemUnits = content.sales_item_units
    })
    if (this.$route.params.duplicate_id) {
      this.fetchDuplicateDeal({id: this.$route.params.duplicate_id}).then((content) => {
        this.forms = content
        this.supplier = content.supplier
        this.location = content.location
        if (content.supplierable_type === 'Emission') {
          this.searchEmissionLocationBy(content.supplierable_id)
        } else if (content.supplierable_type === 'Trader') {
          this.searchTraderLocationBy(content.supplierable_id)
        }
        this.billingAddress = content.billing_address
        if (this.billingAddress) {
          this.billingAddressId = this.billingAddress.id
          this.targetFractionType = this.billingAddress.fraction_type
        }
        this.receivable_types = content.receivable_types
        this.targetSupplierId = content.supplierable_id
        this.targetSupplierType = content.supplierable_type
      })
    } else if (this.$route.params.id) {
      this.fetchEditDeal({id: this.$route.params.id}).then((content) => {
        this.forms = content
        this.supplier = content.supplier
        this.location = content.location
        if (content.supplierable_type === 'Emission') {
          this.searchEmissionLocationBy(content.supplierable_id)
        } else if (content.supplierable_type === 'Trader') {
          this.searchTraderLocationBy(content.supplierable_id)
        }
        this.receivable_types = content.receivable_types
        this.targetSupplierId = content.supplierable_id
        this.targetSupplierType = content.supplierable_type
        this.billingAddress = content.billing_address
        if (this.billingAddress) {
          this.billingAddressId = this.billingAddress.id
          this.targetFractionType = this.billingAddress.fraction_type
        }
      })
    } else {
      this.fetchNewDeal().then((content) => {
        this.forms = content
        this.receivable_types = content.receivable_types
      })
    }
  },
  methods: {
    tradingDateChangeEvent (newValue, oldValue) {

      if (!newValue) {
        return
      }

      this.searchTaxRate()

      this.check_deal_details = this.forms.deal_details.filter(v => v._destroy !== true)

      if (this.check_deal_details.length <= 0) {
        return
      }

      if (!this.forms.supplierable_id) {
        return
      }

      // 初回実行時は以下処理は実行させないため終了
      if (_.isUndefined(oldValue)) { return }

      // 前回の取引日と今回の取引日を比較。
      // 一致しない場合はマスタ情報を取得して

      if( newValue !== oldValue && this.before_trading_date !== newValue) {

        let displayMessage = false
        this.fetchSalesItemPriceDetails({id: this.forms.supplierable_id, type: this.forms.supplierable_type, only_jwnet_item: false, reference_date: newValue}).then((content) => {
          this.salesItemPriceDetails = content.sales_item_price_details

          for (const deal_detail of this.check_deal_details) {
            const salesItemPriceDetail = this.salesItemPriceDetails.find( v => v.code === deal_detail.sales_item_price_code)
            if (salesItemPriceDetail) {
              if (salesItemPriceDetail.name !== deal_detail.sales_item_price_name) {
                displayMessage = true
                break;
              }
              if (salesItemPriceDetail.unit_price != deal_detail.unit_price) {
                displayMessage = true
                break;
              }
              if (salesItemPriceDetail.tax_type !== deal_detail.tax_type) {
                displayMessage = true
                break;
              }
            } else {
              displayMessage = true
              break
            }
          }

          if (displayMessage === true) {
            let modalOption = { title: '確認', message: '売上明細の内容が変更されます。取引日を変更しても良いですか？', options: { width: 380, isCancelButton: true } }
            this.$refs.confirm.open(modalOption).then((confirm) => {
              this.$refs.confirm.close()
              if (confirm) {
                for (const deal_detail of this.forms.deal_details) {

                  const salesItemPriceDetail = this.salesItemPriceDetails.find( v => v.code === deal_detail.sales_item_price_code)
                  if (salesItemPriceDetail) {

                    if (salesItemPriceDetail.name !== deal_detail.sales_item_price_name) {
                      deal_detail.sales_item_price_name = salesItemPriceDetail.name
                    }
                    if (salesItemPriceDetail.unit_price != deal_detail.unit_price) {
                      deal_detail.unit_price = salesItemPriceDetail.unit_price
                      let sum_price = 0
                      // 切り捨て
                      if (this.billingAddress.fraction_type === "rounded_down") {
                        sum_price = Math.floor((deal_detail.quantity * (deal_detail.unit_price / deal_detail.quantity_per_unit) * Math.pow(10, 3)) / Math.pow(10, 3))
                        // 切り上げ
                      } else if (this.billingAddress.fraction_type === "rounded_up") {
                        sum_price = Math.ceil((deal_detail.quantity * (deal_detail.unit_price / deal_detail.quantity_per_unit) * Math.pow(10, 3)) / Math.pow(10, 3))
                        // 四捨五入
                      } else {
                        sum_price = Math.round((deal_detail.quantity * (deal_detail.unit_price / deal_detail.quantity_per_unit) * 1000) / 1000)
                      }
                      deal_detail.detailed_amount = sum_price
                    }
                    if (salesItemPriceDetail.tax_type !== deal_detail.tax_type) {
                      deal_detail.tax_type = salesItemPriceDetail.tax_type
                    }
                  } else {
                    deal_detail._destroy = true
                  }
                  this.calculateAmounts()
                }
              } else {
                this.before_trading_date = oldValue
                this.forms.trading_date = oldValue
              }
            })
          }
        })
      }
    },
    ...mapActions('DriverSalesDeal', {
      fetchNewDeal: 'new',
      fetchEditDeal: 'edit',
    }),
    ...mapActions('DriverSalesDealProvisional', {
      createProvisional: 'create',
      updateProvisional: 'update'
    }),
    ...mapActions('DriverSalesDealReception', {
      createReception: 'create',
      updateReception: 'update',
    }),
    ...mapActions('DriverSalesDealDuplicate', {
      fetchDuplicateDeal: 'new',
    }),
    ...mapActions('DriverMasterSearchSalesStaff', {
      fetchSalesStaffs: 'index',
    }),
    ...mapActions('DriverMasterSearchTaxRate', {
      fetchTaxRate: 'show'
    }),
    /** 売上品目 */
    ...mapActions('DriverMasterSearchSupplierSalesItemPriceDetail', {
      fetchSalesItemPriceDetails: 'index_with_reference_date',
    }),
    /** 売上品目単位 */
    ...mapActions('DriverMasterSearchSalesItemUnit', {
      fetchSalesItemUnits: 'index',
    }),
    /** 排出事業場 */
    ...mapActions('DriverMasterSearchEmissionLocation', {
      fetchEmissionLocations: 'index'
    }),
    /** 業者事業場 */
    ...mapActions('DriverMasterSearchTraderLocation', {
      fetchTraderLocations: 'index',
    }),
    /**
     * 金額計算
     */
    calculateAmounts () {

      // 小計(税抜)
      this.subtotalAmount =  0
      // 消費税金額
      this.salesTaxAmount = 0
      // 非課税対象
      this.taxExemptAmount = 0
      // 合計金額
      this.totalAmount = 0

      if (this.forms) {
        for (const deal_detail of this.forms.deal_details) {
          if (!deal_detail._destroy) {
            if (deal_detail.tax_type === 'on_tax') {
              this.subtotalAmount += parseFloat(deal_detail.detailed_amount)
            } else {
              this.taxExemptAmount += parseFloat(deal_detail.detailed_amount)
            }
          }
        }
        if ((Math.abs(this.subtotalAmount) > 0) && (this.billingAddress)) {

          this.salesTaxAmount = this.subtotalAmount * (this.forms.tax_rate * 0.01)
          // 切り捨て
          if (this.billingAddress.fraction_type === "rounded_down") {
            this.salesTaxAmount = Math.floor(this.salesTaxAmount)
          // 切り上げ
          } else if (this.billingAddress.fraction_type === "rounded_up") {
            this.salesTaxAmount = Math.ceil(this.salesTaxAmount)
          // 四捨五入
          } else {
            this.salesTaxAmount = Math.round(this.salesTaxAmount)
          }
        }
        this.totalAmount = this.subtotalAmount + this.taxExemptAmount + this.salesTaxAmount
      }
    },
    /**
     * 有効な取引明細の件数を算出
     * validationで利用
     * @param list
     */
    setListSize (list) {
      if (list.length > 0) {
        this.listSize = list.length
      } else {
        this.listSize = null
      }
    },
    /**
     * 取引先選択ボタンからのダイアログのコールバック
     * @param item
     */
    choseSupplierCallback (item) {

      this.check_deal_details = this.forms.deal_details.filter(v => v._destroy !== true)

      if ((this.check_deal_details.length > 0) && (this.forms.supplierable_type !== item.type || this.forms.supplierable_id !== item.id)) {
        let modalOption = { title: '確認', message: '取引先が変更されたので売上明細をクリアします。よろしいですか？', options: { width: 380, isCancelButton: true } }
          this.$refs.confirm.open(modalOption).then((confirm) => {
            this.$refs.confirm.close()
            if (confirm) {
              this.forms.supplierable_id = item.id
              this.forms.supplierable_type = item.type
              this.supplier = {
                name: item.name,
                zip_code: item.zip_code,
                address: item.address,
                phone_number: item.phone_number,

              }
              if (item.type === 'Emission') {
                this.searchEmissionLocationBy(item.id)
              } else {
                this.searchTraderLocationBy(item.id)
              }
              this.forms.supplierable_location_code = null
              this.location = null
              this.billingAddressId = null
              this.billingAddress = item.billing_address
              if (this.billingAddress) {
                this.billingAddressId = this.billingAddress.id
                this.targetFractionType = this.billingAddress.fraction_type
              }
              for (let i = 0; i < this.forms.deal_details.length; i++) {
                this.forms.deal_details[i]._destroy = true
              }
            }
          })
      } else {
        this.forms.supplierable_id = item.id
        this.forms.supplierable_type = item.type
        this.supplier = {
          name: item.name,
          zip_code: item.zip_code,
          address: item.address,
          phone_number: item.phone_number,

        }
        if (item.type === 'Emission') {
          this.searchEmissionLocationBy(item.id)
        } else {
          this.searchTraderLocationBy(item.id)
        }
        this.forms.supplierable_location_code = null
        this.location = null

        this.billingAddressId = null
        this.billingAddress = item.billing_address
        if (this.billingAddress) {
          this.billingAddressId = this.billingAddress.id
          this.targetFractionType = this.billingAddress.fraction_type
        }

      }
    },
    supplierClearButtonClickEvent () {
      this.forms.supplierable_id = null
      this.forms.supplierable_type = null
      this.toTraderLocations = []
      this.supplier.name = null
      this.locationableClearButtonClickEvent()
    },
    locationableClearButtonClickEvent () {
      this.forms.supplierable_location_code = null
      this.location = null
    },
    /**
     * 取引数量または単価のチェンジイベント
     */
    quantityOrPriceChangeEvent () {

      let quantity = this.forms.quantity
      let unit_price = this.forms.unit_price
      let quantity_per_unit = this.forms.quantity_per_unit

      if (quantity) {
        if (unit_price) {
          if (quantity_per_unit) {
            let sum_price = 0
            // 切り捨て
            if (this.fractionType === "rounded_down") {
              sum_price = Math.floor((quantity * (unit_price / quantity_per_unit) * Math.pow(10, 3)) / Math.pow(10, 3))
            // 切り上げ
            } else if (this.fractionType === "rounded_up") {
              sum_price = Math.ceil((quantity * (unit_price / quantity_per_unit) * Math.pow(10, 3)) / Math.pow(10, 3))
            // 四捨五入
            } else {
              sum_price = Math.round((quantity * (unit_price / quantity_per_unit) * 1000) / 1000)
            }
            this.forms.detailed_amount = sum_price
          }
        }
      }
    },
    /**
     *
     */
    showDialogButtonClickEvent () {
      this.targetItem = null
      this.targetSupplierId = this.forms.supplierable_id
      this.targetSupplierType = this.forms.supplierable_type
      this.targetDate = this.forms.trading_date
      this.enableDetailDialog = true
    },
    /**
     * キャンセルボタン クリックイベント
     */
    cancelButtonClickEvent () {
      this.$router.push( { name: 'DriverSalesTransactions' } )
    },
    /**
     * 一覧編集ボタン クリックイベント
     * @param item
     */
    editDialogButtonClickEvent (item) {
      this.targetItem = _.cloneDeep(item)
      this.enableDetailDialog = true
    },
    /**
     * 一覧削除ボタン クリックイベント
     * @param item
     */
    deleteButtonClickEvent (item) {
      let modalOption = { title: '削除', message: '削除しますがよろしいですか？', options: { width: 320, titleColor: 'red', isCancelButton: true } }
      this.$refs.confirm.open(modalOption).then((confirm) => {
        this.$refs.confirm.close()
        if (confirm) {
          for (const deal_detail of this.forms.deal_details) {
            if (deal_detail.key === item.key) {
              deal_detail._destroy = true
            }
          }
        }
      })
    },
    searchEmissionLocationBy(emission_id) {
      this.fetchEmissionLocations({id: emission_id}).then((content) => {
        this.toTraderLocations = content.emission_locations
      })
    },
    searchTraderLocationBy(trader_id) {
      this.fetchTraderLocations({id: trader_id}).then((content) => {
        this.toTraderLocations = content.locations
      })
    },
    /**
     * 下書き保存ボタン クリックイベント
     */
    draftSaveButtonClickEvent () {
      const $self = this;
      this.$refs.transactionFormValidator.validate().then((result) => {
        if(result){
          if (this.$route.params.id) {
            setTimeout(function () {
              const params = $self.buildParams()
              $self.updateProvisional({ id: $self.$route.params.id, params: params }).then(() => {
                $self.$router.push({name: 'DriverSalesTransactions'})
              })
            }, 500);
          } else {
            setTimeout(function () {
              const params = $self.buildParams()
              $self.createProvisional({params: params}).then(() => {
                $self.$router.push({name: 'DriverSalesTransactions'})
              })
            }, 500);
          }
        }
      })
    },
    /**
     * 本登録ボタン クリックイベント
     */
    requestButtonClickEvent () {
      const $self = this;
      this.$refs.transactionFormValidator.validate().then((result) => {
        if(result){
          if (this.$route.params.id) {
            setTimeout(function () {
              const params = $self.buildParams()
              $self.updateReception({ id: $self.$route.params.id, params: params }).then((content) => {
                if (content.voucher_url) {
                  window.open(content.voucher_url, "dealPdfTab");
                }
                $self.$router.push({name: 'DriverSalesTransactions'})
              })
            }, 500);
          } else {
            setTimeout(function () {
              const params = $self.buildParams()
              $self.createReception({params: params}).then((content) => {
                if (content.voucher_url) {
                  window.open(content.voucher_url, "dealPdfTab");
                }
                $self.$router.push({name: 'DriverSalesTransactions'})
              })
            }, 500);
          }
        }
      })
    },
    /**
     * ダイアログで反映ボタンが押下された際のコールバック処理
     * @param item
     */
    dialogReflectButtonCallback (item) {
      let reflected = false
      for (let i = 0; i < this.forms.deal_details.length; i++) {
        if (this.forms.deal_details[i].key === item.key) {
          reflected = true
          Object.assign(this.forms.deal_details[i], item)
          this.forms.deal_details[i].tax_rate = this.forms.tax_rate
        }
      }
      if (!reflected) {
        this.forms.deal_details.push(item)
      }
      this.dialogCloseButtonCallback()
      this.calculateAmounts()
    },
    dialogCloseButtonCallback () {
      this.enableDetailDialog = false
      this.enableEmissionDialog = false
      this.enableTraderDialog = false
    },
    buildParams () {
      let dealDetails = []

      // 共有しない場合は請求先テナントはNULLとしてサーバサイドでセットする
      if (this.billingAddress.use_share === false) {
          this.billingAddress.billing_tenant_id = null
      }

      for (const dealDetail of this.forms.deal_details) {
        let params = {
          id: dealDetail.id,
          manifest_id: dealDetail.manifest_id,
          sales_item_price_code: dealDetail.sales_item_price_code,
          sales_item_price_name: dealDetail.sales_item_price_name,
          sales_item_type: dealDetail.sales_item_type,
          item_id: dealDetail.item_id,
          quantity: dealDetail.quantity,
          quantity_per_unit: dealDetail.quantity_per_unit,
          sales_item_unit_id: dealDetail.sales_item_unit_id,
          unit_price: dealDetail.unit_price,
          packing_id: dealDetail.packing_id,
          tax_type: dealDetail.tax_type,
          detailed_amount: dealDetail.detailed_amount,
          detailed_memo: dealDetail.detailed_memo,
          _destroy: dealDetail._destroy,
        }
        dealDetails.push(params)
      }
      return {

        supplierable_type: this.forms.supplierable_type,
        supplierable_id: this.forms.supplierable_id,
        supplierable_location_code: this.forms.supplierable_location_code,
        billing_address_id: this.billingAddress.id,
        code: this.billingAddress.code,
        name: this.billingAddress.name,
        zip_code: this.billingAddress.zip_code,
        prefecture_code: this.billingAddress.prefecture_code,
        address_1: this.billingAddress.address_1,
        address_2: this.billingAddress.address_2,
        address_3: this.billingAddress.address_3,
        phone_number: this.billingAddress.phone_number,
        close_day_type: this.billingAddress.close_day_type,
        payment_site: this.billingAddress.payment_site,
        payment_day_type: this.billingAddress.payment_day_type,
        payment_method_type: this.billingAddress.payment_method_type,
        counterbalance_type: this.billingAddress.counterbalance_type,
        billing_tenant_id: this.billingAddress.billing_tenant_id,
        fraction_type: this.billingAddress.fraction_type,
        register_number: this.billingAddress.register_number,
        trading_date: this.forms.trading_date,
        sales_date: this.forms.sales_date,
        sales_staff: this.forms.sales_staff,
        receivable_type: this.forms.receivable_type,
        issue_voucher: this.forms.issue_voucher,
        tax_rate: this.forms.tax_rate,
        subtotal_excluding_tax_amount: this.subtotalAmount,
        sales_tax_amount: this.salesTaxAmount,
        tax_exempt_amount: this.taxExemptAmount,
        total_amount: this.totalAmount,
        deal_details_attributes: dealDetails,
      }
    },
    searchTaxRate() {
      this.fetchTaxRate({target_date: this.forms.trading_date}).then((content) => {
        if (this.syncTransactionDetails.length > 0 ) {
          if (this.forms.tax_rate !== null) {
            if (this.forms.tax_rate !== content.rate) {
              let modalOption = { title: '確認', message: '前回登録後に有効な税率が変更されましたので、税額を再計算し画面表示します。編集後に必ず保存してください。', options: { width: 500, titleColor: 'info', isCancelButton: false } }
              this.$refs.confirm.open(modalOption).then((confirm) => {
                this.$refs.confirm.close()
                if (confirm) {
                  this.forms.tax_rate = content.rate
                  this.calculateAmounts()
                }
              })
            }
          }
        } else {
          this.forms.tax_rate = content.rate
        }
      })
    },
    /**
     * 取引日変更　コールバック
     */
    tradingDateChangedCallback (value) {
      this.forms.trading_date = value;
    },
    /**
     * 売上計上日変更　コールバック
     */
    salesDateChangedCallback (value) {
      this.forms.sales_date = value;
    }
  },
}
</script>

<style scoped lang="scss">
</style>
